import type { ChatbotMessage, ChatbotMetric, TimelineStage } from '../types/chatbot-message.types';
import { ExperimentStatus } from '../types/experiment.types';
import type { Experiment, ExperimentKpis, TimelineCheckpoint } from '../types/experiment.types';

/**
 * Builds the cumulative scripted-narration message list for the experiment
 * chatbot Timeline view. Every numeric or per-arm metric here is data-derived;
 * when the source (`experiment.metrics`, `kpis`, …) is missing the matching
 * field / metric block is skipped rather than substituted with a placeholder.
 *
 * The scripted scaffolding (Day dividers + generic lifecycle prose with no
 * numbers) stays in place so the timeline still reads like a narrative; the
 * mock-laden numeric fallbacks that used to live here are gone and only
 * resurface in MSW dev/demo mode via real fixtures hitting these same
 * data inputs.
 */

interface NarrationInput {
  experiment?: Experiment | undefined;
  kpis?: ExperimentKpis | undefined;
  timeline?: TimelineCheckpoint[] | undefined;
}

interface BuildInput extends NarrationInput {
  stage: TimelineStage;
}

function day0({ experiment }: NarrationInput): ChatbotMessage[] {
  const out: ChatbotMessage[] = [
    { kind: 'day-divider', id: 'd0-divider', day: 'Day 0', label: 'Experiment Launched' },
  ];
  const body = formatDay0Body(experiment);
  if (body) {
    out.push({
      kind: 'status',
      id: 'd0-status',
      tone: 'success',
      badge: 'Deployed',
      title: 'Your experiment is being set up.',
      body,
    });
  }
  out.push({
    kind: 'next-step',
    id: 'd0-next',
    body: 'Day 1 will begin once data in GMC is complete and validated. No action needed from you.',
  });
  return out;
}

function day1(): ChatbotMessage[] {
  return [
    { kind: 'day-divider', id: 'd1-divider', day: 'Day 1', label: 'Product Distribution Check' },
    {
      kind: 'status',
      id: 'd1-status',
      tone: 'success',
      badge: 'Validated',
      title: 'Your experiment is live.',
      body: 'Data in GMC has been validated across both arms. Traffic is now officially flowing and your experiment has started.',
    },
    {
      kind: 'next-step',
      id: 'd1-next',
      body: 'Lily will monitor impression distribution over the next 24 hours.',
    },
    { kind: 'inline-icon-actions', id: 'd1-icons', count: 9 },
  ];
}

function day2(): ChatbotMessage[] {
  return [
    { kind: 'day-divider', id: 'd2-divider', day: 'Day 2', label: 'Product Distribution Check' },
    {
      kind: 'status',
      id: 'd2-status',
      tone: 'success',
      badge: 'Parity confirmed',
      title: 'Both arms are receiving traffic.',
      body: 'Lily will continuously monitor your experiment and confirm ramp-up on Day 3. No action needed from you.',
    },
    { kind: 'inline-icon-actions', id: 'd2-icons', count: 9 },
  ];
}

function day3(): ChatbotMessage[] {
  return [
    { kind: 'day-divider', id: 'd3-divider', day: 'Day 3', label: 'Full Ramp Confirmed' },
    {
      kind: 'status',
      id: 'd3-status',
      tone: 'success',
      badge: 'Ramp-up Confirmed',
      title: 'Your experiment is running cleanly.',
      body: 'Both arms passed traffic parity. Performance data is now accumulating.',
    },
    {
      kind: 'next-step',
      id: 'd3-next',
      eyebrow: 'Coming up on Day 7',
      body: 'Lily will surface early trend signals and flag if any action is needed from you.',
    },
    { kind: 'inline-icon-actions', id: 'd3-icons', count: 9 },
  ];
}

function day7Common({ experiment }: NarrationInput): ChatbotMessage | null {
  const metrics: ChatbotMetric[] = [];
  pushLiftMetric(metrics, 'CTR lift', '(Arm B vs A)', experiment?.metrics.ctrLift);
  if (metrics.length === 0) return null;
  return {
    kind: 'status',
    id: 'd7-status',
    tone: 'warning',
    badge: 'Input Needed',
    title: 'Early trends are in.',
    metrics,
  };
}

function day7Pending(input: NarrationInput): ChatbotMessage[] {
  const out: ChatbotMessage[] = [
    { kind: 'day-divider', id: 'd7-divider', day: 'Day 7', label: 'Early Signal Review' },
  ];
  const status = day7Common(input);
  if (status) out.push(status);
  out.push({
    kind: 'recommendation',
    id: 'd7-rec',
    body: 'Lily recommends continuing to Day 28 to reach significance.',
    subBody: 'Do you want to:',
    actions: [
      { id: 'proceed', label: 'Proceed', variant: 'primary' },
      { id: 'pause-and-adjust', label: 'Pause & Adjust', variant: 'secondary' },
      { id: 'end', label: 'End', variant: 'tertiary' },
    ],
  });
  return out;
}

function day7Proceeded(input: NarrationInput): ChatbotMessage[] {
  const out: ChatbotMessage[] = [
    { kind: 'day-divider', id: 'd7-divider', day: 'Day 7', label: 'Early Signal Review' },
  ];
  const status = day7Common(input);
  if (status) out.push(status);
  out.push({
    kind: 'recommendation',
    id: 'd7-rec-proceeded',
    body: 'Lily recommends continuing to Day 28 to reach significance.',
    subBody: 'Do you want to:',
    actions: [{ id: 'proceed', label: 'Proceed', variant: 'primary' }],
  });
  return out;
}

function day17({ experiment }: NarrationInput): ChatbotMessage[] {
  const out: ChatbotMessage[] = [
    { kind: 'day-divider', id: 'd17-divider', day: 'Day 17', label: 'Mid-Run Health Check' },
  ];
  const metrics: ChatbotMetric[] = [];
  pushLiftMetric(metrics, 'CTR lift', '(Arm B)', experiment?.metrics.ctrLift);
  pushLiftMetric(metrics, 'Revenue per click', '(Arm B)', experiment?.metrics.revenueLift);
  if (metrics.length > 0) {
    out.push({
      kind: 'status',
      id: 'd17-status',
      tone: 'success',
      badge: 'Aligned',
      title: 'Performance alignment confirmed.',
      body: 'Your experiment is on track.',
      metrics,
    });
  }
  out.push({
    kind: 'narrative',
    id: 'd17-narr',
    text: 'No action needed. Lily will surface final results on Day 28.',
  });
  return out;
}

function day28({ experiment }: NarrationInput): ChatbotMessage[] {
  const ctrLift = formatLift(experiment?.metrics.ctrLift);
  const ctrTrend = inferRowTrend(experiment?.metrics.ctrLift);
  const revLift = formatLift(experiment?.metrics.revenueLift);
  const revTrend = inferRowTrend(experiment?.metrics.revenueLift);

  const rows: {
    label: string;
    left: string;
    right: string;
    rightTrend?: 'up' | 'down' | 'neutral';
  }[] = [];
  if (ctrLift) {
    rows.push({
      label: 'CTR',
      left: 'Baseline',
      right: ctrLift,
      ...(ctrTrend ? { rightTrend: ctrTrend } : {}),
    });
  }
  if (revLift) {
    rows.push({
      label: 'Revenue Per Click',
      left: 'Baseline',
      right: revLift,
      ...(revTrend ? { rightTrend: revTrend } : {}),
    });
  }

  const out: ChatbotMessage[] = [
    { kind: 'day-divider', id: 'd28-divider', day: 'Day 28', label: 'Results Readout' },
    {
      kind: 'narrative',
      id: 'd28-intro',
      text: "Here's how the two arms performed across your primary and secondary metrics.",
    },
  ];

  if (rows.length > 0) {
    out.push({
      kind: 'comparison',
      id: 'd28-cmp',
      tone: 'success',
      badge: 'Results Ready',
      columns: { left: 'Arm A', subLeft: '(Control)', right: 'Arm B', subRight: '(Variant)' },
      rows,
    });
  }

  out.push({ kind: 'narrative', id: 'd28-wrap', text: 'Your experiment wraps up today.' });
  out.push({
    kind: 'next-step',
    id: 'd28-next',
    body: "You have 7 days to make your next move - Scale the winning variant to your full catalog or close out the experiment here. Take a look at the full readout when you're ready.",
  });
  out.push({
    kind: 'actions',
    id: 'd28-actions',
    actions: [
      { id: 'extend', label: 'Extend The Experiment', variant: 'primary' },
      { id: 'end', label: 'End The Experiment', variant: 'secondary' },
    ],
  });
  return out;
}

function day287({ experiment }: NarrationInput): ChatbotMessage[] {
  const out: ChatbotMessage[] = [
    {
      kind: 'day-divider',
      id: 'd28-7-divider',
      day: 'Day 28 + 7',
      label: 'Roll Out or End the Experiment',
    },
  ];

  const metrics: ChatbotMetric[] = [];
  pushLiftMetric(metrics, 'CTR lift', undefined, experiment?.metrics.ctrLift);
  pushLiftMetric(metrics, 'Revenue per click', undefined, experiment?.metrics.revenueLift);

  out.push({
    kind: 'recommendation',
    id: 'd28-7-rec',
    tone: 'warning',
    badge: 'Input Needed',
    body: 'Time to decide what happens next.',
    ...(metrics.length > 0 ? { metrics } : {}),
    actions: [
      { id: 'scale-winner', label: 'Scale Arm B to full catalog', variant: 'primary' },
      { id: 'extend', label: 'Extend The Experiment', variant: 'secondary' },
      { id: 'end-without-scaling', label: 'End Without Scaling', variant: 'tertiary' },
    ],
  });
  return out;
}

/** Returns the cumulative scripted-narration message list up to (and including) the given stage. */
export function buildTimelineNarration({
  stage,
  experiment,
  kpis,
  timeline,
}: BuildInput): ChatbotMessage[] {
  const input: NarrationInput = { experiment, kpis, timeline };
  // Day 0 stands alone — emitting day1's "Your experiment is live" /
  // "validated traffic flowing" copy at stage 0 would lie about
  // unlaunched DRAFT / CREATED experiments.
  const out: ChatbotMessage[] = [...day0(input)];
  if (stage === 'day-0') return finalize(out, experiment);

  out.push(...day1());
  if (stage === 'day-1') return finalize(out, experiment);

  out.push(...day2());
  if (stage === 'day-2') return finalize(out, experiment);

  out.push(...day3());
  if (stage === 'day-3') return finalize(out, experiment);

  if (stage === 'day-7') {
    out.push(...day7Pending(input));
    return finalize(out, experiment);
  }
  if (stage === 'day-7-proceeded') {
    out.push(...day7Proceeded(input));
    return finalize(out, experiment);
  }

  // Day 17+ implies the user proceeded past the Day-7 checkpoint.
  out.push(...day7Proceeded(input), ...day17(input));
  if (stage === 'day-17') return finalize(out, experiment);

  out.push(...day28(input));
  if (stage === 'day-28') return finalize(out, experiment);

  out.push(...day287(input));
  return finalize(out, experiment);
}

/**
 * Appends a terminal narrative when the experiment ended before its natural
 * conclusion — CANCELLED (operator stopped via POST /stop) or REJECTED
 * (operator declined at the approval gate, BUG-029). Both freeze the
 * timeline at the last meaningful checkpoint; the copy differs to reflect
 * the distinct reason.
 */
function finalize(
  messages: ChatbotMessage[],
  experiment: Experiment | undefined,
): ChatbotMessage[] {
  if (experiment?.status === ExperimentStatus.REJECTED) {
    return [
      ...messages,
      {
        kind: 'narrative',
        id: 'rejected',
        text: 'This experiment was declined at the approval gate. The timeline above reflects progress up to that point.',
      },
    ];
  }
  if (experiment?.status === ExperimentStatus.CANCELLED) {
    return [
      ...messages,
      {
        kind: 'narrative',
        id: 'cancelled',
        text: 'This experiment was cancelled. The timeline above reflects progress up to that point.',
      },
    ];
  }
  return messages;
}

/** Push a CTR/revenue lift metric onto the bucket when the source value is real; skip when undefined/null. */
function pushLiftMetric(
  bucket: ChatbotMetric[],
  label: string,
  hint: string | undefined,
  value: number | null | undefined,
): void {
  const formatted = formatLift(value);
  if (!formatted) return;
  const trend = inferTrend(value);
  bucket.push({
    label,
    ...(hint ? { hint } : {}),
    value: formatted,
    ...(trend ? { trend } : {}),
  });
}

/** Format a percent-unit lift number (e.g. `4.2`) as a signed string ("+4.2%"); returns null when the source value is missing so callers can skip the field. */
function formatLift(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const sign = value > 0 ? '+' : '';
  return `${sign}${String(value)}%`;
}

/** Map a number to a metric trend treatment. Undefined when the source value is missing so callers can skip the trend prop. */
function inferTrend(value: number | null | undefined): 'up' | 'down' | 'neutral' | undefined {
  if (value === null || value === undefined) return undefined;
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'neutral';
}

/** Same as `inferTrend` but returns the comparison-row trend slot. */
function inferRowTrend(value: number | null | undefined): 'up' | 'down' | 'neutral' | undefined {
  return inferTrend(value);
}

/**
 * Assemble the Day-0 deployment body from `experiment` fields only — when
 * none of SKU count / arm count are populated yet, omit the message
 * entirely rather than render an empty sentence.
 */
function formatDay0Body(experiment: Experiment | undefined): string | null {
  const sku = experiment?.skuCount?.toLocaleString();
  const arms = experiment?.armCount;
  // `armCount` defaults to 0 in the mapper when the DTO has no
  // numberOfArms — treat that as "unknown" so we don't render
  // "split into 0 arms (even split)".
  const hasArms = arms != null && arms > 0;
  if (sku == null && !hasArms) return null;
  const armCopy = hasArms ? `${String(arms)} arms (${formatSplit(arms)})` : 'their arms';
  const skuCopy = sku != null ? `${sku} SKUs have been` : 'Your SKUs have been';
  return `${skuCopy} split into ${armCopy} and deployed to your GMC Supplemental feed. Lily is now validating that both arms are configured correctly within GMC.`;
}

/** Render the per-arm split caption. Only the most common splits get explicit copy; everything else falls through to "even split". */
function formatSplit(armCount: number): string {
  if (armCount === 2) return '50/50';
  if (armCount === 3) return '33/33/33';
  if (armCount === 4) return '25/25/25/25';
  return 'even split';
}

/** Ordered list of every supported stage — used by the dev stage selector. */
export const ALL_TIMELINE_STAGES: { value: TimelineStage; label: string }[] = [
  { value: 'day-0', label: 'Day 0 · Experiment Launched' },
  { value: 'day-1', label: 'Day 1 · Product Distribution Check' },
  { value: 'day-2', label: 'Day 2 · Parity Confirmed' },
  { value: 'day-3', label: 'Day 3 · Full Ramp Confirmed' },
  { value: 'day-7', label: 'Day 7 · Early Signal Review (Input Needed)' },
  { value: 'day-7-proceeded', label: 'Day 7 · After Proceed' },
  { value: 'day-17', label: 'Day 17 · Mid-Run Health Check' },
  { value: 'day-28', label: 'Day 28 · Results Readout' },
  { value: 'day-28-7', label: 'Day 28 + 7 · Roll Out Decision' },
];

export type { NarrationInput };
